import React from "react";
import csjPhoto from "../assets/csjPhoto.png";

function OrientationAideJuridique() {
  return (
    <div className="min-h-screen bg-gray-100">
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="px-6 py-8 mt-4 bg-blue-50 rounded-xl border border-blue-100 shadow-sm mb-8">
          <h1 className="text-2xl font-bold text-blue-700 mb-3 text-center">
            <span className="inline-block h-1 bg-warning w-8 mr-2 rounded-full align-middle"></span>
            Orientation pour l'aide juridique
            <span className="inline-block h-1 bg-warning w-8 ml-2 rounded-full align-middle"></span>
          </h1>
          <p className="text-center text-blue-600 max-w-3xl mx-auto">
            Guide simple pour contacter rapidement le bureau d'aide juridique compétent pour votre dossier en droit civil au Québec.
          </p>
        </div>

        {/* Main content */}
        <div className="space-y-6">
          {/* Étape 1 */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <h2 className="text-xl font-semibold text-blue-700 mb-4 flex items-center">
              <span className="bg-blue-50 p-2 rounded-full mr-2">1️⃣</span>
              Trouver votre bureau d'aide juridique
            </h2>
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Rendez-vous sur la page officielle de la Commission des services juridiques :
              </p>
              <a
                href="https://www.csj.qc.ca/commission-des-services-juridiques/nous-joindre/bureaux-d-aide-juridique/fr"
                target="_blank"
                rel="noreferrer"
                className="block p-4 bg-blue-50 rounded-lg text-blue-700 hover:bg-blue-100 transition-colors"
              >
                www.csj.qc.ca/commission-des-services-juridiques/nous-joindre/bureaux-d-aide-juridique/fr
              </a>
              <p className="text-sm text-slate-600">
                Entrez les trois premiers caractères de votre code postal. Le site vous indiquera le bureau qui dessert votre zone, 
                <strong> spécifiquement en droit civil</strong> (ex. "(CIVIL)" à côté du nom).
              </p>
              <div className="bg-gray-50 border-l-4 border-blue-200 p-4 rounded-r-lg">
                <p className="text-sm text-slate-600 italic">
                  Pour un dossier de séparation, divorce, pension ou garde, c'est bien en droit civil qu'il faut être dirigé, 
                  sauf indication contraire.
                </p>
              </div>
              {/* Image placeholder */}
              <div className="bg-slate-100 border border-slate-200 rounded-lg p-4 text-center overflow-hidden">
                <img 
                  src={csjPhoto} 
                  alt="Capture d'écran du site de la CSJ montrant la recherche par code postal" 
                  className="max-w-full h-auto mx-auto"
                />
              </div>
            </div>
          </div>

          {/* Étape 2 */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <h2 className="text-xl font-semibold text-blue-700 mb-4 flex items-center">
              <span className="bg-blue-50 p-2 rounded-full mr-2">2️⃣</span>
              Appeler pour prendre rendez-vous
            </h2>
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Une fois le bureau compétent identifié, <strong>téléphonez</strong> au numéro indiqué.
                Essayez d'appeler tôt le matin (8h30 - 9h30) pour avoir plus de chance de parler rapidement à la réceptionniste.
              </p>
              <div className="bg-blue-50 border-l-4 border-blue-200 p-4 rounded-r-lg">
                <p className="text-sm text-blue-700">
                  <strong>Que dire à la réception :</strong>
                </p>
                <ul className="mt-2 space-y-2 text-sm text-blue-700">
                  <li>• Précisez que c'est la première fois que vous appelez</li>
                  <li>• Indiquez que vous souhaitez consulter un avocat pour un dossier familial</li>
                  <li>• Demandez la date de rendez-vous la plus proche</li>
                  <li>• <strong>Important :</strong> Si votre situation est urgente (date de cour prochaine), mentionnez-le immédiatement</li>
                </ul>
                <p className="mt-2 text-sm text-blue-700 italic">
                  Il n'est pas nécessaire de raconter les détails de votre dossier à la réceptionniste.
                </p>
              </div>
              <div className="bg-green-50 border-l-4 border-green-200 p-4 rounded-r-lg">
                <p className="text-sm text-green-700">
                  Vous obtiendrez un <strong>premier rendez-vous</strong> (environ 15 à 20 minutes) où l'avocat(e) évaluera :
                </p>
                <ul className="mt-2 space-y-2 text-sm text-green-700">
                  <li>• Votre admissibilité financière</li>
                  <li>• La nature de votre problème (garde, pension, séparation, etc.)</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Étape 3 */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <h2 className="text-xl font-semibold text-blue-700 mb-4 flex items-center">
              <span className="bg-blue-50 p-2 rounded-full mr-2">3️⃣</span>
              Le jour du rendez-vous
            </h2>
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Le bureau d'aide juridique vous aura envoyé une liste de <strong>documents à apporter</strong>.
                Assurez-vous d'avoir :
              </p>
              <div className="bg-slate-50 p-4 rounded-lg">
                <h3 className="font-semibold text-slate-700 mb-2">Documents financiers requis :</h3>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-start">
                    <span className="text-blue-500 mr-2">•</span>
                    <span><strong>Si vous travaillez ou avez d'autres revenus :</strong></span>
                  </li>
                  <ul className="pl-6 space-y-1 text-sm text-slate-600">
                    <li>- Avis de cotisation provincial le plus récent</li>
                    <li>- Déclaration de revenus provinciale</li>
                    <li>- Derniers talons de paie</li>
                    <li>- Prestations d'assurance-chômage (si applicable)</li>
                    <li>- Relevés bancaires des 3 derniers mois</li>
                  </ul>
                  <li className="flex items-start mt-2">
                    <span className="text-blue-500 mr-2">•</span>
                    <span><strong>Si vous êtes sur l'aide sociale :</strong></span>
                  </li>
                  <ul className="pl-6 space-y-1 text-sm text-slate-600">
                    <li>- Uniquement votre carnet d'aide sociale (la vérification a déjà été faite)</li>
                  </ul>
                </ul>
              </div>
              <div className="bg-yellow-50 border-l-4 border-yellow-200 p-4 rounded-r-lg">
                <p className="text-sm text-yellow-700">
                  <strong>Important :</strong> Arrivez à l'heure — ce rendez-vous initial sert à vérifier votre admissibilité 
                  et à recueillir les informations essentielles pour la suite.
                </p>
              </div>
            </div>
          </div>

          {/* Résultats possibles */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <h2 className="text-xl font-semibold text-blue-700 mb-4 flex items-center">
              <span className="bg-blue-50 p-2 rounded-full mr-2">📋</span>
              Résultats possibles
            </h2>
            <div className="space-y-4">
              <div className="grid gap-4 md:grid-cols-3">
                {/* Admis gratuit */}
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <h3 className="font-semibold text-green-700 mb-2">Admis gratuitement</h3>
                  <p className="text-sm text-green-600">
                    L'avocat(e) vous contactera pour collecter les documents et clarifier vos demandes.
                  </p>
                </div>

                {/* Admis avec contribution */}
                <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                  <h3 className="font-semibold text-yellow-700 mb-2">Admis avec contribution</h3>
                  <p className="text-sm text-yellow-600">
                    Contribution unique (max. ±800 $), possiblement échelonnée.
                  </p>
                </div>

                {/* Refusé */}
                <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                  <h3 className="font-semibold text-red-700 mb-2">Refusé</h3>
                  <p className="text-sm text-red-600">
                    Possibilité de contester la décision auprès de la Commission.
                  </p>
                </div>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-200 p-4 rounded-r-lg mt-4">
                <p className="text-sm text-blue-700">
                  <strong>En cas d'urgence</strong> (violence, fuite de domicile, etc.), mentionnez-le dès l'appel : 
                  le bureau pourra accélérer le rendez-vous ou vous rediriger vers des ressources immédiates.
                </p>
              </div>
            </div>
          </div>

          {/* Résumé */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <h2 className="text-xl font-semibold text-blue-700 mb-4 flex items-center">
              <span className="bg-blue-50 p-2 rounded-full mr-2">✨</span>
              En résumé
            </h2>
            <div className="space-y-4">
              <ol className="list-decimal list-inside text-sm text-slate-600 space-y-2">
                <li>Trouvez le <strong>bureau d'aide juridique</strong> (CIVIL) qui couvre votre secteur</li>
                <li>Appelez tôt, prenez un rendez-vous de 15-20 minutes</li>
                <li>Apportez les <strong>documents demandés</strong> (preuves de revenus, etc.)</li>
                <li>Après évaluation, vous pourriez être <strong>admis gratuitement</strong>, <strong>admis avec contribution</strong>, 
                ou <strong>refusé</strong> (avec possibilité de contestation)</li>
              </ol>
            </div>
          </div>
        </div>

        {/* Legal note */}
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm text-slate-600 mt-8">
          <p className="italic text-center">
            Note : Ces informations sont fournies à titre indicatif. Les procédures peuvent varier selon les régions 
            et les circonstances particulières.
          </p>
        </div>
      </main>
    </div>
  );
}

export default OrientationAideJuridique;
